<template>
  <page-layout>
    <page-header :breadcrumb="['阅读统计数据优化']" />

    <page-main>
      <div class="panel-header">
        <div class="title">优化配置</div>
      </div>

      <el-card shadow="never" class="info-card" v-loading="configLoading">
        <div class="stat-grid">
          <div class="stat-item">
            <div class="stat-label">机构名称</div>
            <div class="stat-value">{{ organizationName }}</div>
          </div>
          <div class="stat-item">
            <div class="stat-label">设备数量</div>
            <div class="stat-value">{{ deviceCount }}</div>
          </div>
        </div>

        <div class="switch-row">
          <span class="label">开启优化</span>
          <el-switch v-model="optimizeEnabled" />
        </div>

        <div class="checkbox-row">
          <div class="stat-label">隐藏统计项</div>
          <el-checkbox-group v-model="hiddenStatisticValues">
            <el-checkbox
              v-for="item in hiddenStatisticOptions"
              :key="item.value"
              :label="item.value"
            >
              {{ item.label }}
            </el-checkbox>
          </el-checkbox-group>
        </div>

        <div class="save-row">
          <el-button
            type="primary"
            size="small"
            :loading="configSaving"
            @click="onSaveConfig"
          >
            保存
          </el-button>
        </div>
      </el-card>
    </page-main>

    <page-main>
      <div class="panel-header action-header">
        <div class="title">优化数据</div>
        <el-button type="primary" size="small" @click="onAddClick">
          新增
        </el-button>
      </div>

      <el-table v-loading="tableLoading" :data="tableData" border>
        <el-table-column prop="year" label="年份" width="100" />
        <el-table-column prop="name" label="优化数据" />
        <el-table-column label="操作" width="140">
          <template #default="{ row }">
            <el-button type="text" size="small" @click="onDownloadClick(row)">
              下载
            </el-button>
            <el-button type="text" size="small" @click="onReuploadClick(row)">
              重新上传
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </page-main>

    <el-dialog
      :title="addDialogTitle"
      :visible.sync="addDialogVisible"
      width="520px"
      :close-on-click-modal="false"
      @close="resetAddDialog"
    >
      <el-form label-width="90px">
        <el-form-item label="年份" required>
          <el-select
            v-model="addForm.year"
            placeholder="请选择年份"
            :disabled="addDialogMode === 'reupload'"
            style="width: 100%"
          >
            <el-option
              v-for="year in yearOptions"
              :key="year"
              :label="String(year)"
              :value="year"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="文件" required>
          <el-upload
            ref="templateUpload"
            action="#"
            :auto-upload="false"
            :show-file-list="true"
            :before-upload="beforeTemplateUpload"
            :on-change="onTemplateFileChange"
            :on-remove="onTemplateFileRemove"
            :limit="1"
            accept=".xls,.xlsx"
          >
            <el-button size="small" type="primary">选择文件</el-button>
            <div slot="tip" class="el-upload__tip">
              仅支持 xls/xlsx，且不超过10MB
            </div>
          </el-upload>
        </el-form-item>
      </el-form>

      <div slot="footer" class="dialog-footer">
        <el-button
          class="left-btn"
          type="text"
          :disabled="!addForm.year"
          @click="onDownloadTemplateClick"
        >
          下载模板
        </el-button>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="onSubmitAdd">
          提交
        </el-button>
      </div>
    </el-dialog>
  </page-layout>
</template>

<script lang="ts">
import { defaultErrorHandler } from '@/admin-shared-modules/utils'
import { GET, POST } from '@/admin-shared-modules/utils/ajax'
import { MessageService } from '@/admin-shared-modules/utils/message.service'

const START_YEAR = 2018

const getYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const list = []
  for (let year = currentYear; year >= START_YEAR; year -= 1) {
    list.push(year)
  }
  return list
}

export default {
  data() {
    return {
      configLoading: false,
      configSaving: false,
      tableLoading: false,
      optimizeEnabled: true,
      hiddenStatisticValues: [],
      organizationName: '示例机构',
      deviceCount: 36,
      page: 0,
      pageSize: 10,
      total: 0,
      addDialogVisible: false,
      addDialogMode: 'add',
      uploading: false,
      yearOptions: getYearOptions(),
      addForm: {
        year: null,
        file: null
      },
      tableData: [],
      hiddenStatisticOptions: [
        { label: '图书借阅统计', value: 'bookSendTops' },
        { label: '读者阅读统计', value: 'readingReport' },
        { label: '读者排行', value: 'userReadTop' }
      ]
    }
  },

  async mounted() {
    await Promise.all([this.fetchConfig(), this.fetchTableData()])
  },

  computed: {
    addDialogTitle() {
      return this.addDialogMode === 'reupload'
        ? '重新上传优化数据'
        : '新增优化数据'
    }
  },

  methods: {
    async fetchConfig() {
      this.configLoading = true
      try {
        const { data } = await GET('fake_stastics/config', {})

        this.organizationName = data.name || ''
        this.deviceCount = data.device_count || 0
        this.optimizeEnabled = !!data.fake_statistic
        this.hiddenStatisticValues = Array.isArray(data.hidden_statistic)
          ? data.hidden_statistic
          : []
      } finally {
        this.configLoading = false
      }
    },

    async fetchTableData() {
      this.tableLoading = true
      try {
        const { data } = await GET('fake_stastics/list', {
          data: {
            page: this.page,
            pageSize: this.pageSize
          }
        })
        const list = data.items || []

        this.page = Number(data.page) || 0
        this.pageSize = Number(data.pageSize) || this.pageSize
        this.total = Number(data.total) || 0

        this.tableData = list.map((item: string) => ({
          year: item,
          name: `${item}年度.xlsx`
        }))
      } finally {
        this.tableLoading = false
      }
    },

    async onSaveConfig() {
      this.configSaving = true
      try {
        await POST('fake_stastics/config', {
          data: {
            fake_statistic: this.optimizeEnabled,
            hidden_statistic: this.hiddenStatisticValues
          }
        })
        MessageService.open({ type: 'success', message: '保存成功' })
      } catch (e) {
        defaultErrorHandler(e)
      } finally {
        this.configSaving = false
      }
    },

    onAddClick() {
      this.addDialogMode = 'add'
      this.resetAddForm()
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.clearTemplateUploadFiles()
      })
    },

    onReuploadClick(row: { year: string | number }) {
      this.addDialogMode = 'reupload'
      this.resetAddForm()
      const year = Number(row.year)
      this.addForm.year = this.yearOptions.includes(year) ? year : null
      this.addDialogVisible = true
      this.$nextTick(() => {
        this.clearTemplateUploadFiles()
      })
    },

    resetAddForm() {
      this.addForm = {
        year: null,
        file: null
      }
    },

    resetAddDialog() {
      this.resetAddForm()
      this.clearTemplateUploadFiles()
      this.uploading = false
    },

    clearTemplateUploadFiles() {
      const uploadRef = this.$refs.templateUpload as any
      if (uploadRef && uploadRef.clearFiles) {
        uploadRef.clearFiles()
      }
    },

    beforeTemplateUpload(file: File) {
      const isExcel = /\.(xls|xlsx)$/i.test(file.name)
      if (!isExcel) {
        MessageService.open({ type: 'error', message: '仅支持 xls/xlsx 文件' })
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        MessageService.open({ type: 'error', message: '文件大小不能超过10MB' })
        return false
      }

      return false
    },

    onTemplateFileChange(file: any) {
      if (!file || !file.raw) return

      const isExcel = /\.(xls|xlsx)$/i.test(file.name)
      const isLt10M = file.raw.size / 1024 / 1024 < 10
      if (!isExcel || !isLt10M) return

      this.addForm.file = file.raw
    },

    onTemplateFileRemove() {
      this.addForm.file = null
    },

    getFilenameFromHeader(contentDisposition: string) {
      if (!contentDisposition) return ''

      const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)
      if (utf8Match && utf8Match[1]) {
        return decodeURIComponent(utf8Match[1])
      }

      const filenameMatch = contentDisposition.match(/filename="?([^";]+)"?/i)
      if (filenameMatch && filenameMatch[1]) {
        return decodeURIComponent(filenameMatch[1])
      }

      return ''
    },

    async downloadBlob(
      api: string,
      params: { year: number | string },
      fallbackFilename: string
    ) {
      const res = (await GET(api, {
        data: params,
        responseType: 'blob'
      })) as any

      const blob = res.data as Blob
      const contentDisposition =
        (res.headers && res.headers['content-disposition']) || ''
      const filename =
        this.getFilenameFromHeader(contentDisposition) || fallbackFilename

      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      link.click()
      window.URL.revokeObjectURL(link.href)
    },

    async onDownloadClick(row: { year: string; name: string }) {
      try {
        await this.downloadBlob(
          'fake_stastics/export',
          { year: row.year },
          row.name
        )
      } catch (e) {
        defaultErrorHandler(e)
      }
    },

    async onDownloadTemplateClick() {
      if (!this.addForm.year) return
      try {
        const fallbackFilename = `${this.addForm.year}年度模板.xlsx`
        await this.downloadBlob(
          'fake_stastics/download_template',
          { year: this.addForm.year },
          fallbackFilename
        )
      } catch (e) {
        defaultErrorHandler(e)
      }
    },

    async onSubmitAdd() {
      if (!this.addForm.year) {
        MessageService.open({ type: 'warning', message: '请选择年份' })
        return
      }
      if (!this.addForm.file) {
        MessageService.open({ type: 'warning', message: '请先选择文件' })
        return
      }

      this.uploading = true
      try {
        const formData = new FormData()
        formData.append('file', this.addForm.file)

        const { data: res } = await POST(
          `fake_stastics/upload_template?year=${this.addForm.year}`,
          {
            data: formData,
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        )
        MessageService.open({ message: res?.message || '上传成功' })
        this.addDialogVisible = false
        await this.fetchTableData()
      } catch (e) {
        defaultErrorHandler(e)
      } finally {
        this.uploading = false
      }
    }
  }
}
</script>

<style scoped>
.switch-row {
  display: flex;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
}

.label {
  margin-right: 12px;
  color: #606266;
}

.stat-grid {
  display: flex;
  gap: 48px;
}

.stat-item {
  min-width: 220px;
}

.stat-label {
  margin-bottom: 8px;
  color: #909399;
  font-size: 13px;
}

.stat-value {
  color: #303133;
  font-size: 16px;
  font-weight: 600;
}

.checkbox-row {
  margin-top: 24px;
}

.save-row {
  margin-top: 24px;
}

.action-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-footer .left-btn {
  float: left;
}
</style>
